import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Plus, Trash2, Edit2, Copy, Search, Archive, FileText } from 'lucide-react';
import { API, BANK_CATEGORIES, PAGE_SIZE } from './constants';
import { ConfirmDialog } from './ConfirmDialog';

export const BankPanel = () => {
  const { getAuthHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [category, setCategory] = useState('all');
  const [query, setQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [form, setForm] = useState({ title: '', content: '', category: 'caption' });

  const fetchPage = useCallback(async (skip) => {
    const params = new URLSearchParams();
    if (category !== 'all') params.set('category', category);
    if (query) params.set('q', query);
    params.set('skip', String(skip));
    params.set('limit', String(PAGE_SIZE));
    const res = await axios.get(`${API}/content/bank?${params.toString()}`, getAuthHeaders());
    return res.data;
  }, [getAuthHeaders, category, query]);

  const refresh = useCallback(async () => {
    try {
      const data = await fetchPage(0);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setHasMore(!!data.has_more);
    } catch (e) { console.error(e); }
  }, [fetchPage]);

  useEffect(() => {
    const t = setTimeout(refresh, 200);
    return () => clearTimeout(t);
  }, [refresh]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const data = await fetchPage(items.length);
      setItems(prev => [...prev, ...(data.items || [])]);
      setHasMore(!!data.has_more);
    } catch (e) { toast.error('Failed to load more'); }
    finally { setLoadingMore(false); }
  };

  const openNew = () => {
    setEditing(null);
    setForm({ title: '', content: '', category: 'caption' });
    setDialogOpen(true);
  };
  const openEdit = (item) => {
    setEditing(item);
    setForm({ title: item.title || '', content: item.content, category: item.category });
    setDialogOpen(true);
  };
  const save = async () => {
    if (!form.content.trim()) return toast.error('Content required');
    try {
      if (editing) {
        await axios.put(`${API}/content/bank/${editing.item_id}`, form, getAuthHeaders());
      } else {
        await axios.post(`${API}/content/bank`, form, getAuthHeaders());
      }
      toast.success('Saved');
      setDialogOpen(false);
      refresh();
    } catch (e) { toast.error('Failed'); }
  };
  const remove = async () => {
    if (!pendingDeleteId) return;
    try {
      await axios.delete(`${API}/content/bank/${pendingDeleteId}`, getAuthHeaders());
      toast.success('Deleted');
      refresh();
    } catch (e) {
      toast.error('Failed');
    } finally {
      setPendingDeleteId(null);
    }
  };
  const duplicate = async (id) => {
    try {
      await axios.post(`${API}/content/bank/${id}/duplicate`, {}, getAuthHeaders());
      toast.success('Duplicated');
      refresh();
    } catch (e) { toast.error('Failed to duplicate'); }
  };
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch (e) {
      toast.error('Copy failed');
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder="Search saved content..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="bank-search"
          />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-36" data-testid="bank-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {BANK_CATEGORIES.map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Button size="sm" className="w-full bg-sky-600 hover:bg-sky-700" onClick={openNew} data-testid="bank-add-btn">
        <Plus className="w-4 h-4 mr-1" /> Save Reusable Content
      </Button>

      {items.length === 0 ? (
        <Card>
          <CardContent className="text-center py-8">
            <Archive className="w-8 h-8 mx-auto mb-2 text-sky-400" />
            <p className="text-sm text-muted-foreground">
              {query || category !== 'all' ? 'No content matches your filters.' : 'Save reusable captions, hashtag sets, CTAs, and templates.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <p className="text-xs text-muted-foreground text-right" data-testid="bank-count">
            Showing {items.length} of {total}
          </p>
          <div className="space-y-2">
            {items.map(it => (
              <Card key={it.item_id} className="border-sky-100 dark:border-sky-900">
                <CardContent className="p-3">
                  <div className="flex justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <Badge variant="secondary" className="capitalize text-[10px]">{it.category}</Badge>
                      {it.title && <p className="font-semibold text-sm truncate">{it.title}</p>}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => copyToClipboard(it.content)} title="Copy" data-testid={`bank-copy-${it.item_id}`}>
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => duplicate(it.item_id)} title="Duplicate" data-testid={`bank-duplicate-${it.item_id}`}>
                        <FileText className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(it)} data-testid={`bank-edit-${it.item_id}`}>
                        <Edit2 className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => setPendingDeleteId(it.item_id)} data-testid={`bank-delete-${it.item_id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm whitespace-pre-wrap break-words text-muted-foreground">{it.content}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          {hasMore && (
            <Button
              variant="outline"
              className="w-full border-sky-300 text-sky-600"
              onClick={loadMore}
              disabled={loadingMore}
              data-testid="bank-load-more"
            >
              {loadingMore ? 'Loading...' : `Load ${Math.min(PAGE_SIZE, total - items.length)} more`}
            </Button>
          )}
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? 'Edit Content' : 'Save Reusable Content'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="bank-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BANK_CATEGORIES.map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Title (optional)</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Signature CTA — book a call"
                data-testid="bank-title-input"
              />
            </div>
            <div>
              <Label>Content *</Label>
              <Textarea
                value={form.content} rows={6}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                placeholder="Your caption, hashtag set, CTA, or template..."
                data-testid="bank-content-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-sky-600 hover:bg-sky-700" onClick={save} data-testid="bank-save-btn">
              {editing ? 'Update' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!pendingDeleteId}
        onOpenChange={(o) => !o && setPendingDeleteId(null)}
        onConfirm={remove}
        title="Delete this content?"
        description="This reusable content will be permanently removed from your bank."
        testId="bank-confirm-delete"
      />
    </div>
  );
};

export default BankPanel;
